import { useState } from 'react'

import SaveIcon from '@mui/icons-material/Save'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import InputAdornment from '@mui/material/InputAdornment'
import TextField from '@mui/material/TextField'

import { SelectObject } from '../../../core/components/SelectObject'
import { SelectString } from '../../../core/components/SelectString'
import { StyledError } from '../../../theme/components/StyledError'
import { useUpdateBijlageTemplate } from '../../hooks/useUpdateBijlageTemplate'
import type { BijlageTemplate } from '../../types/BijlageTemplate'
import {
  REFERENCES_ALLOWING_WAITFOR,
  getAvailableReferenceOptions,
  getInitialWaitForCandidate,
  getWaitForCandidates,
  hasBijlageTemplatePlanning,
  referenceOptions,
} from '../../util'

interface Props {
  bijlageTemplate: BijlageTemplate
  templateSet?: BijlageTemplate[]
  onClose: () => void
}

const IS_NEGATIVE_DIFF = true

export const EditBijlageTemplate = ({
  bijlageTemplate,
  templateSet = [],
  onClose,
}: Props) => {
  const { documentsoort, projectType } = bijlageTemplate
  // planning bestaat alleen op project templates, de api weigert het voor andere scopes
  const isProject = bijlageTemplate.scope === 'project'

  const [errors, setErrors] = useState({
    reference: '',
    herplanDiff: '',
  })

  const initialReferenceOption =
    referenceOptions(projectType).find(
      (opt) => opt.code === bijlageTemplate.reference,
    ) || null

  const [reference, setReference] = useState(initialReferenceOption)
  const [isBeforeReference, setIsBeforeReference] = useState<boolean>(
    bijlageTemplate.herplanDiff
      ? bijlageTemplate.herplanDiff < 0
      : IS_NEGATIVE_DIFF,
  )
  const [herplanDiff, setHerplanDiff] = useState<any>(
    bijlageTemplate.herplanDiff ? Math.abs(bijlageTemplate.herplanDiff) : '',
  )
  const [sortOrder, setSortOrder] = useState<number>(bijlageTemplate.sortOrder)

  const waitForCandidates = getWaitForCandidates(templateSet, bijlageTemplate)
  const [waitForCandidate, setWaitForCandidate] =
    useState<BijlageTemplate | null>(
      getInitialWaitForCandidate(waitForCandidates, bijlageTemplate.waitFor),
    )

  const availableReferenceOptions = getAvailableReferenceOptions(
    waitForCandidate,
    projectType,
  )

  const {
    mutate: updateBijlageTemplate,
    isPending,
    error,
  } = useUpdateBijlageTemplate()

  const hasPlanning = isProject && hasBijlageTemplatePlanning(bijlageTemplate)

  const onClickRemovePlanning = () => {
    updateBijlageTemplate(
      {
        bijlageTemplate: {
          _id: bijlageTemplate._id,
          reference: null,
          herplanDiff: null,
          waitFor: null,
        },
      },
      {
        onSuccess: onClose,
      },
    )
  }

  const onClickConfirm = () => {
    if (!isProject) {
      updateBijlageTemplate(
        { bijlageTemplate: { _id: bijlageTemplate._id, sortOrder } },
        { onSuccess: onClose },
      )
      return
    }

    if (validate()) {
      updateBijlageTemplate(
        {
          bijlageTemplate: {
            _id: bijlageTemplate._id,
            // null = expliciete clear-intentie. Backend valideert de
            // resulterende combinatie en weigert ongeldige varianten.
            reference: reference ? reference.code : null,
            herplanDiff: herplanDiff
              ? isBeforeReference
                ? /* v8 ignore next - TODO: missing coverage -- @preserve */
                  -herplanDiff
                : /* v8 ignore next - TODO: missing coverage -- @preserve */
                  herplanDiff
              : null,
            waitFor: waitForCandidate ? waitForCandidate.documentsoort : null,
            sortOrder,
          },
        },
        {
          onSuccess: onClose,
        },
      )
    }
  }

  function validate() {
    if (
      (herplanDiff && !reference) ||
      (!herplanDiff && herplanDiff !== 0 && reference) // ! en !== 0 omdat 0 een geldige waarde is
    ) {
      setErrors({
        ...errors,
        reference: 'Vul zowel herplanverschil als referentie in',
        herplanDiff: 'Vul zowel herplanverschil als referentie in',
      })

      return false
    }

    // sortOrder zit op alle templates en kan niet gecleared worden. geen check nodig

    return true
  }

  return (
    <Dialog maxWidth="md" fullWidth open>
      <DialogTitle>Aanpassen {documentsoort}</DialogTitle>
      <DialogContent>
        <StyledError error={error} />
        {isProject && (
          <>
            <Box
              sx={{
                mb: 1.5,
                width: '100%',
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
              }}
            >
              <TextField
                sx={{ flex: 2 }}
                variant="standard"
                type="number"
                value={herplanDiff}
                onChange={(ev) => {
                  setHerplanDiff(Number(ev.target.value))
                  setErrors({
                    ...errors,
                    herplanDiff: '',
                    reference: '',
                  })
                }}
                label="Herplanverschil"
                error={!!errors.herplanDiff}
                helperText={errors.herplanDiff}
                slotProps={{
                  input: {
                    endAdornment: (
                      <>
                        <InputAdornment sx={{ ml: 2 }} position="end">
                          {herplanDiff === 1 ? 'week' : 'weken'}
                        </InputAdornment>
                      </>
                    ),
                  },
                  htmlInput: {
                    min: 0,
                    max: 52,
                    style: {
                      appearance: 'textfield', // zodat de arrow up/down knopjes verdwijnen, zodat de inputadorment mooier is
                    },
                  },
                }}
              />
              <SelectString
                sx={{ flex: 1 }}
                value={isBeforeReference ? 'voor' : 'na'}
                onChange={(option) => setIsBeforeReference(option === 'voor')}
                options={['voor', 'na']}
                disabled={!herplanDiff && !reference}
                disableClearable
                label="voor/na"
                data-testid="herplan-diff-autocomplete"
              />
              <SelectObject
                sx={{ flex: 3 }}
                value={reference}
                onChange={(option) => {
                  setReference(option)
                  setErrors({
                    ...errors,
                    reference: '',
                    herplanDiff: '',
                  })
                  if (option === null) {
                    setHerplanDiff('')
                  }
                }}
                options={availableReferenceOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.code === value.code
                }
                error={!!errors.reference}
                helperText={errors.reference}
                label="Referentie"
              />
            </Box>
            <Box sx={{ mb: 1.5 }}>
              <SelectObject
                value={waitForCandidate}
                onChange={(option) => {
                  setWaitForCandidate(option)
                  // Selecting a waitFor narrows the reference set; clear an
                  // incompatible reference so the user must reselect explicitly.
                  if (
                    option &&
                    reference &&
                    !REFERENCES_ALLOWING_WAITFOR.includes(reference.code)
                  ) {
                    setReference(null)
                    setHerplanDiff('')
                  }
                }}
                options={waitForCandidates}
                getOptionLabel={(option) => option.documentsoort}
                isOptionEqualToValue={(option, value) =>
                  option._id === value._id
                }
                label="Wacht op afronden"
              />
            </Box>
          </>
        )}
        <TextField
          variant="standard"
          type="number"
          value={sortOrder}
          label="Sortering"
          onChange={(ev) => setSortOrder(Number(ev.target.value))}
        />
      </DialogContent>
      <DialogActions>
        {hasPlanning && (
          <Button
            onClick={onClickRemovePlanning}
            variant="outlined"
            color="error"
            disabled={isPending}
            sx={{ mr: 'auto' }}
          >
            Verwijder planning
          </Button>
        )}
        <Button onClick={onClose} variant="text">
          Annuleren
        </Button>

        <Button
          onClick={onClickConfirm}
          color="primary"
          variant="contained"
          disabled={isPending}
          startIcon={<SaveIcon />}
          endIcon={isPending && <CircularProgress size={20} />}
        >
          Template opslaan
        </Button>
      </DialogActions>
    </Dialog>
  )
}
